import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';

const sanitizeOpts = { ALLOWED_TAGS: ['p', 'b', 'strong', 'i', 'em', 'span', 'br'], ALLOWED_ATTR: ['style'] };
const contentSx = {
    color: "white",
    fontSize: "2.8rem",
    lineHeight: 1.2,
    fontWeight: "500",
    letterSpacing: "0.02em",
    wordBreak: "break-word",
    whiteSpace: "pre-line",
    "& p": { m: 0, lineHeight: 1.2 },
    "& p + p": { mt: 0.2 }
};

export default function WorkoutInfoFullScreen({
    open,
    onClose,
    cls,
    isLoading = false,
    workoutImage = null
}) {
    const [showAllTracks, setShowAllTracks] = useState(false);
    const [isImageZoomOpen, setImageZoomOpen] = useState(false);

    const rxContent = cls?.description || "";
    const intContent = cls?.descriptionIntermediate || "";
    const begContent = cls?.descriptionBeginner || "";

    const hasMultipleTracks = !!(intContent || begContent);
    const displayRx = rxContent || intContent || begContent;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            fullScreen
            slotProps={{ root: { sx: { zIndex: 2100 } } }}
        >
            <DialogContent
                sx={{
                    position: "relative",
                    backgroundColor: "#121212",
                    color: "white",
                    p: 0,
                    m: 0,
                    height: "100vh",
                    width: "100vw",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column"
                }}
            >
                {isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0, left: 0, width: '100%', height: '100%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 999
                        }}
                    >
                        <CircularProgress size={60} />
                    </Box>
                )}

                {/* Top bar: vasakul Show all/RX, paremal X nupp */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        paddingTop: "calc(8px + env(safe-area-inset-top, 0px))",
                        zIndex: 2000,
                        backgroundColor: "rgba(0,0,0,0.3)"
                    }}
                >
                    {hasMultipleTracks ? (
                        <Box
                            component="button"
                            onClick={() => setShowAllTracks((v) => !v)}
                            sx={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                width: 120,
                                height: 32,
                                border: "none",
                                background: "rgba(255,255,255,0.2)",
                                borderRadius: "16px",
                                cursor: "pointer",
                                overflow: "hidden",
                                "&:hover": { background: "rgba(255,255,255,0.25)" }
                            }}
                        >
                            {/* Ringikujuline thumb - liigub vasakule/paremale */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: showAllTracks ? "calc(100% - 28px)" : "4px",
                                    top: "4px",
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    backgroundColor: "white",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                    transition: "left 0.2s ease"
                                }}
                            />
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    pointerEvents: "none"
                                }}
                            >
                                <Typography component="span" sx={{ fontSize: "0.75rem", color: "white", fontWeight: 500 }}>
                                    {showAllTracks ? "Show RX" : "Show all"}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box />
                    )}
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: "white",
                            backgroundColor: "rgba(255,255,255,0.15)",
                            "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                {/* WOD name and type */}
                <Box
                    sx={{
                        px: 3,
                        pt: 2,
                        position: "absolute",
                        top: { xs: "calc(48px + env(safe-area-inset-top, 0px))", md: "52px" },
                        left: 4,
                    }}
                >
                    {cls?.wodName && (
                        <Typography sx={{ fontWeight: "bold", color: "#ff9800", mb: 1, fontSize: { xs: "0.8rem", sm: "1.0rem", md: "1.2rem" }, lineHeight: 1.2 }}>
                            🔥 {cls.wodName}
                        </Typography>
                    )}
                    {cls?.wodType && cls.trainingType === "WOD" && cls.wodType !== "NONE" && (
                        <Typography sx={{ color: "#64b5f6", mb: 2, fontStyle: "italic", fontSize: { xs: "0.6rem", sm: "0.8rem", md: "1.0rem" }, fontWeight: "500" }}>
                            {cls.wodType}
                        </Typography>
                    )}
                </Box>

                {/* Content */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        flex: 1,
                        px: 3,
                        overflow: "auto",
                        paddingTop: { xs: "calc(48px + 70px + env(safe-area-inset-top, 0px))", md: "120px" },
                        paddingBottom: "20px"
                    }}
                >
                    <Box sx={{ width: "100%", maxWidth: "100%" }}>
                        {!!workoutImage?.imageData && (
                            <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                                <Box
                                    component="img"
                                    src={workoutImage.imageData}
                                    alt="Workout"
                                    onClick={() => setImageZoomOpen(true)}
                                    sx={{
                                        width: "100%",
                                        maxWidth: 900,
                                        maxHeight: "55vh",
                                        objectFit: "contain",
                                        borderRadius: 1,
                                        border: "1px solid rgba(255,255,255,0.3)",
                                        backgroundColor: "rgba(0,0,0,0.35)",
                                        cursor: "zoom-in"
                                    }}
                                />
                            </Box>
                        )}
                        {displayRx && (
                            showAllTracks && hasMultipleTracks ? (
                                <Box sx={{ display: "flex", gap: 0, width: "100%", maxWidth: "100%" }}>
                                {rxContent && (
                                    <>
                                        <Box sx={{ flex: 1, minWidth: 0, px: 2 }}>
                                            <Typography sx={{ color: "#ff9800", fontWeight: "bold", mb: 1, fontSize: "1.2rem" }}>RX</Typography>
                                            <Box
                                                component="div"
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rxContent, sanitizeOpts) }}
                                                sx={contentSx}
                                            />
                                        </Box>
                                        {(intContent || begContent) && <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.3)" }} />}
                                    </>
                                )}
                                {intContent && (
                                    <>
                                        <Box sx={{ flex: 1, minWidth: 0, px: 2 }}>
                                            <Typography sx={{ color: "#64b5f6", fontWeight: "bold", mb: 1, fontSize: "1.2rem" }}>INT</Typography>
                                            <Box
                                                component="div"
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(intContent, sanitizeOpts) }}
                                                sx={contentSx}
                                            />
                                        </Box>
                                        {begContent && <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.3)" }} />}
                                    </>
                                )}
                                {begContent && (
                                    <Box sx={{ flex: 1, minWidth: 0, px: 2 }}>
                                        <Typography sx={{ color: "#81c784", fontWeight: "bold", mb: 1, fontSize: "1.2rem" }}>BEG</Typography>
                                        <Box
                                            component="div"
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(begContent, sanitizeOpts) }}
                                            sx={contentSx}
                                        />
                                    </Box>
                                )}
                                </Box>
                            ) : (
                                <Box
                                    component="div"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(rxContent || intContent || begContent, sanitizeOpts)
                                    }}
                                    sx={{ ...contentSx, maxWidth: "100%" }}
                                />
                            )
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <Dialog
                open={isImageZoomOpen}
                onClose={() => setImageZoomOpen(false)}
                maxWidth="lg"
                fullWidth
                slotProps={{ root: { sx: { zIndex: 2200 } } }}
            >
                <Box sx={{ position: "relative", backgroundColor: "black" }}>
                    <IconButton
                        onClick={() => setImageZoomOpen(false)}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            zIndex: 1,
                            color: "white",
                            backgroundColor: "rgba(0,0,0,0.45)",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={workoutImage?.imageData || ""}
                        alt="Workout full size"
                        sx={{
                            width: "100%",
                            maxHeight: "90vh",
                            objectFit: "contain",
                            backgroundColor: "black"
                        }}
                    />
                </Box>
            </Dialog>
        </Dialog>
    );
}

